import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LogOut, ArrowLeft, Clock, Truck,
  Shield, ClipboardCheck, ChevronRight, FileCheck, AlertTriangle
} from "lucide-react";
import { Logo } from "@/components/Logo";

export default function DashboardPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, l] = await Promise.all([
          api.get(`/reports/${reportId}`),
          api.get("/lookups"),
        ]);
        setReport(r);
        setLookups(l);
      } catch (err) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reportId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!report) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Report not found</div>;

  const siteName = lookups.sites?.find(s => s.id === report.site_id)?.name || "—";
  const isReadOnly = report.status === "Submitted";

  const modules = [
    {
      label: "Equipment Hours",
      icon: Clock,
      count: report.equipment_hours?.length || 0,
      path: `/report/${reportId}/equipment-hours`,
      color: "text-blue-400",
    },
    {
      label: "Delays",
      icon: AlertTriangle,
      count: report.delay_events?.length || 0,
      path: `/report/${reportId}/delays`,
      color: "text-orange-400",
    },
    {
      label: "Material Movements",
      icon: Truck,
      count: report.material_movements?.length || 0,
      path: `/report/${reportId}/material-movements`,
      color: "text-amber-400",
    },
    {
      label: "Safety & Environment",
      icon: Shield,
      count: report.safety && Object.keys(report.safety).length > 0 ? 1 : 0,
      path: `/report/${reportId}/safety-quality`,
      color: "text-red-400",
    },
    {
      label: "Quality Notes",
      icon: ClipboardCheck,
      count: report.quality_notes?.length || 0,
      path: `/report/${reportId}/safety-quality`,
      color: "text-purple-400",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate("/home")} data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Home
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight">Daily Report</h1>
            <p className="text-muted-foreground text-sm mt-1">
              <span className="font-mono">{report.report_date}</span>
              <span className="mx-2">|</span>{siteName}
              <span className="mx-2">|</span>{report.shift} Shift
            </p>
          </div>
          <Badge variant="outline" className={
            isReadOnly
              ? "bg-primary/20 text-primary border-primary/30"
              : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
          }>
            {report.status}
          </Badge>
        </div>

        {/* Shift time info */}
        <Card className="border-border bg-card mb-6">
          <CardContent className="py-4 px-5 flex items-center gap-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift Start</p>
              <p className="font-mono text-sm mt-1">
                {report.shift_start ? new Date(report.shift_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
              </p>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift End</p>
              <p className="font-mono text-sm mt-1">
                {report.shift_end ? new Date(report.shift_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Pending"}
              </p>
            </div>
            {report.foreman && (
              <>
                <div className="h-8 w-px bg-border" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Foreman</p>
                  <p className="text-sm mt-1">{report.foreman}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit Header link */}
        {!isReadOnly && (
          <Button
            variant="outline"
            size="sm"
            className="mb-6 border-border text-muted-foreground"
            onClick={() => navigate(`/report/${reportId}/header`)}
            data-testid="edit-header-button"
          >
            Edit Header Info
          </Button>
        )}

        {/* Module Cards */}
        <div className="space-y-3">
          {modules.map((mod) => (
            <Card
              key={mod.label}
              className="border-border bg-card hover:bg-accent/20 transition-colors cursor-pointer"
              onClick={() => navigate(mod.path)}
              data-testid={`module-card-${mod.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="py-4 px-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <mod.icon className={`h-5 w-5 ${mod.color}`} strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-medium">{mod.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <span className="kpi-value">{mod.count}</span> {mod.count === 1 ? "entry" : "entries"}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Review & Submit */}
        <div className="mt-8">
          <Button
            onClick={() => navigate(`/report/${reportId}/review`)}
            data-testid="review-submit-button"
            className="w-full font-headline font-semibold h-11"
          >
            <FileCheck className="h-4 w-4 mr-2" strokeWidth={2} />
            Review & Submit
          </Button>
        </div>
      </main>
    </div>
  );
}
